import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SocratesLogo } from '@/components/SocratesLogo';
import { useToast } from '@/hooks/use-toast';
import { useWebAuthn } from '@/hooks/useWebAuthn';
import { Loader2, Mail, Fingerprint, ChevronDown, ChevronUp } from 'lucide-react';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [passkeyLoading, setPasskeyLoading] = useState(false);

  const otpInputRef = useRef<HTMLInputElement>(null);

  const { user, loading: authLoading, signIn, signInWithMagicLink, signInWithPasskey, verifyOtp } = useAuth();
  const { isSupported: passkeysSupported, authenticateWithPasskey, clearError: clearPasskeyError } = useWebAuthn();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();

  const redirectParam = searchParams.get('redirect');
  const stateFrom = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname;
  const from = redirectParam || stateFrom || '/dashboard';

  // Redirect if already logged in
  useEffect(() => {
    if (!authLoading && user) {
      navigate(from, { replace: true });
    }
  }, [user, authLoading, navigate, from]);

  // Focus OTP input when it appears
  useEffect(() => {
    if (otpSent && otpInputRef.current) {
      otpInputRef.current.focus();
    }
  }, [otpSent]);

  const handleSendOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      toast({
        title: 'Email required',
        description: 'Please enter your email address.',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);

    const redirectUrl = from !== '/dashboard' ? `${window.location.origin}${from}` : undefined;
    const { error } = await signInWithMagicLink(email, redirectUrl);

    if (error) {
      toast({
        title: 'Failed to send code',
        description: error.message,
        variant: 'destructive',
      });
      setLoading(false);
      return;
    }

    setOtpSent(true);
    setLoading(false);
    toast({
      title: 'Check your email!',
      description: 'Enter the 6-digit code or click the sign-in link.',
    });
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!otpCode || otpCode.length !== 6) {
      toast({
        title: 'Invalid code',
        description: 'Please enter the 6-digit code from your email.',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);
    const { error } = await verifyOtp(email, otpCode);

    if (error) {
      toast({
        title: 'Verification failed',
        description: error.message,
        variant: 'destructive',
      });
      setLoading(false);
      return;
    }

    toast({
      title: 'Welcome back!',
      description: 'You have successfully signed in.',
    });
    navigate(from, { replace: true });
  };

  const handlePasswordLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    const { error } = await signIn(email, password);

    if (error) {
      toast({
        title: 'Login failed',
        description: error.message,
        variant: 'destructive',
      });
      setLoading(false);
      return;
    }

    toast({
      title: 'Welcome back!',
      description: 'You have successfully logged in.',
    });
    navigate(from, { replace: true });
  };

  const handlePasskeyLogin = async () => {
    clearPasskeyError();
    setPasskeyLoading(true);

    const result = await authenticateWithPasskey();

    if (!result.success) {
      toast({
        title: 'Passkey sign-in failed',
        description: result.error || 'Authentication was cancelled or failed.',
        variant: 'destructive',
      });
      setPasskeyLoading(false);
      return;
    }

    if (result.tokenHash && result.email) {
      const { error } = await signInWithPasskey(result.tokenHash, result.email);

      if (error) {
        toast({
          title: 'Sign-in failed',
          description: error.message,
          variant: 'destructive',
        });
        setPasskeyLoading(false);
        return;
      }

      toast({
        title: 'Welcome back!',
        description: 'You have successfully signed in with your passkey.',
      });

      navigate(from, { replace: true });
      return;
    } else {
      toast({
        title: 'Sign-in failed',
        description: 'Failed to complete passkey authentication.',
        variant: 'destructive',
      });
    }

    setPasskeyLoading(false);
  };

  const handleResendOtp = async () => {
    setLoading(true);
    const redirectUrl = from !== '/dashboard' ? `${window.location.origin}${from}` : undefined;
    const { error } = await signInWithMagicLink(email, redirectUrl);
    setLoading(false);

    if (error) {
      toast({
        title: 'Failed to resend',
        description: error.message,
        variant: 'destructive',
      });
      return;
    }

    toast({
      title: 'Code resent!',
      description: 'Check your email for a new code.',
    });
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-50 to-stone-100 dark:from-zinc-950 dark:to-zinc-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-stone-400" />
      </div>
    );
  }

  // OTP verification screen
  if (otpSent) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-50 to-stone-100 dark:from-zinc-950 dark:to-zinc-900 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-zinc-900 p-8 rounded-lg shadow-xl max-w-md w-full">
          <div className="text-center mb-6">
            <div className="flex justify-center mb-4">
              <div className="bg-emerald-100 dark:bg-emerald-900 p-3 rounded-full">
                <Mail className="w-8 h-8 text-emerald-600 dark:text-emerald-400" />
              </div>
            </div>
            <h2 className="text-2xl font-bold text-zinc-800 dark:text-zinc-100 mb-2">Check your email</h2>
            <p className="text-zinc-600 dark:text-zinc-400">
              We sent a 6-digit code to <strong>{email}</strong>.
              Enter it below or click the link in your email.
            </p>
          </div>

          <form onSubmit={handleVerifyOtp} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="otp-code">Verification code</Label>
              <Input
                ref={otpInputRef}
                id="otp-code"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                placeholder="000000"
                value={otpCode}
                onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="text-center text-2xl tracking-[0.5em] font-mono"
                autoComplete="one-time-code"
              />
            </div>

            <Button type="submit" className="w-full" disabled={loading || otpCode.length !== 6}>
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Verifying...
                </>
              ) : (
                'Verify & Sign In'
              )}
            </Button>
          </form>

          <div className="mt-4 space-y-2">
            <Button
              variant="ghost"
              onClick={handleResendOtp}
              disabled={loading}
              className="w-full text-sm"
            >
              Didn't get the code? Resend
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                setOtpSent(false);
                setOtpCode('');
              }}
              className="w-full text-sm"
            >
              Use a different email
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 to-stone-100 dark:from-zinc-950 dark:to-zinc-900 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-zinc-900 p-8 rounded-lg shadow-xl max-w-md w-full">
        <div className="text-center mb-6">
          <div className="flex justify-center mb-4">
            <SocratesLogo size="large" />
          </div>
          <h1 className="text-2xl font-bold text-zinc-800 dark:text-zinc-100 font-caveat">Socrates</h1>
          <p className="text-zinc-600 dark:text-zinc-400">Sign in to your account</p>
        </div>

        {/* Primary: Email OTP */}
        <form onSubmit={handleSendOtp} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
            />
          </div>

          <Button type="submit" className="w-full" disabled={loading}>
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Sending code...
              </>
            ) : (
              <>
                <Mail className="mr-2 h-4 w-4" />
                Send me a sign-in code
              </>
            )}
          </Button>
        </form>

        {/* Passkey option */}
        {passkeysSupported && (
          <>
            <div className="relative my-4">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-zinc-200 dark:border-zinc-700" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="bg-white dark:bg-zinc-900 px-2 text-zinc-500 dark:text-zinc-400">or</span>
              </div>
            </div>

            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={handlePasskeyLogin}
              disabled={passkeyLoading || loading}
            >
              {passkeyLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Authenticating...
                </>
              ) : (
                <>
                  <Fingerprint className="mr-2 h-4 w-4" />
                  Sign in with Passkey
                </>
              )}
            </Button>
          </>
        )}

        {/* Secondary: Password option */}
        <div className="mt-6">
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="flex items-center justify-center gap-1 w-full text-sm text-zinc-500 dark:text-zinc-400
                       hover:text-zinc-700 dark:hover:text-zinc-300 transition-colors"
          >
            {showPassword ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            Use password instead
          </button>

          {showPassword && (
            <form onSubmit={handlePasswordLogin} className="mt-4 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email-pw">Email</Label>
                <Input
                  id="email-pw"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">Password</Label>
                  <Link
                    to="/forgot-password"
                    className="text-sm text-zinc-600 dark:text-zinc-400 hover:text-zinc-800 dark:hover:text-zinc-200"
                  >
                    Forgot password?
                  </Link>
                </div>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                />
              </div>

              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  'Sign in with password'
                )}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
